// Global error model. Backend RPCs raise typed SQLSTATE/message codes (e.g. the
// reporting REPORT_TIMEOUT → SQLSTATE 57014, and EXPORT_TOO_LARGE). The UI maps them to
// stable AppErrorKind values so feedback surfaces can render Georgian messages later
// (Phase 6.2+) without re-parsing raw driver errors.
export type AppErrorKind =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'timeout'
  | 'too_large'
  | 'network'
  | 'invalid_payload'
  | 'stale_write'
  | 'idempotency_conflict'
  | 'unknown';

export class AppError extends Error {
  readonly kind: AppErrorKind;
  readonly code: string | undefined;

  constructor(kind: AppErrorKind, message: string, code?: string, cause?: unknown) {
    super(message, cause === undefined ? undefined : { cause });
    this.name = 'AppError';
    this.kind = kind;
    this.code = code;
  }
}

interface PostgrestLikeError {
  message?: string;
  code?: string;
}

function asPostgrestError(value: unknown): PostgrestLikeError | null {
  if (typeof value === 'object' && value !== null) {
    return value;
  }
  return null;
}

export function toAppError(value: unknown): AppError {
  if (value instanceof AppError) {
    return value;
  }

  const pg = asPostgrestError(value);
  const code = pg?.code;
  const message = pg?.message ?? 'Unexpected error';

  // SQLSTATE 57014 = query_canceled (statement/lock timeout) → reporting REPORT_TIMEOUT.
  if (code === '57014' || message.includes('REPORT_TIMEOUT')) {
    return new AppError('timeout', message, code, value);
  }
  if (message.includes('EXPORT_TOO_LARGE')) {
    return new AppError('too_large', message, code, value);
  }
  // Admin mutation RPC canonical codes (migrations 0043-0046) — reusable by any future
  // optimistic-concurrency mutation UI, not just Administration (design §10).
  if (message.includes('ADMIN_STALE_WRITE')) {
    return new AppError('stale_write', message, code, value);
  }
  if (message.includes('IDEMPOTENCY_CONFLICT')) {
    return new AppError('idempotency_conflict', message, code, value);
  }
  if (message.includes('ADMIN_INVALID_PAYLOAD')) {
    return new AppError('invalid_payload', message, code, value);
  }
  // 42501 = insufficient_privilege (RLS / authorize deny).
  if (code === '42501' || message.includes('FORBIDDEN') || message.includes('ADMIN_SCOPE_FORBIDDEN')) {
    return new AppError('forbidden', message, code, value);
  }

  return new AppError('unknown', message, code, value);
}

/**
 * Codes for a file the engine rejected on a size/decompression limit. The Import Gateway
 * reports these inside a `400 INVALID_FILE` envelope, but the honest kind is `too_large`.
 */
const TOO_LARGE_CODES = new Set(['FILE_TOO_LARGE', 'ZIP_DECOMPRESSED_TOO_LARGE']);

/**
 * Map an Import Gateway Edge failure (HTTP status + sanitized machine code) onto the existing
 * AppErrorKind union (D-075). The boundary's own codes stay ad hoc by design — D-067 assigned
 * this remapping to the frontend adapter, and this is the single place it happens.
 *
 * `issueCodes` are the per-issue codes carried by a `400 INVALID_FILE` body; they are the only
 * place a size rejection is visible, so they participate in the 400 decision.
 *
 * 409 splits deliberately: repeating an operation the server has already completed is an
 * idempotency conflict, whereas every other conflict (lease lost, another attempt in progress,
 * blocked, wrong lifecycle state) means the client's view of the batch went stale and it must
 * re-read before acting.
 */
export function edgeErrorToAppErrorKind(
  status: number,
  code: string | null,
  issueCodes: readonly string[] = [],
): AppErrorKind {
  switch (status) {
    case 401:
      return 'unauthorized';
    case 403:
      return 'forbidden';
    case 404:
      return 'not_found';
    case 408:
    case 504:
      return 'timeout';
    case 413:
      return 'too_large';
    case 409:
      return code === 'ALREADY_COMMITTED' ? 'idempotency_conflict' : 'stale_write';
    case 400: {
      const tooLarge =
        (code !== null && TOO_LARGE_CODES.has(code)) || issueCodes.some((c) => TOO_LARGE_CODES.has(c));
      return tooLarge ? 'too_large' : 'invalid_payload';
    }
    default:
      return 'unknown';
  }
}
